import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

interface Failure { id: string; created_at: string; kind: string; to: string; reason: string }
interface EmailStatus { resend_configured: boolean; failures: Failure[] }

export function AdminEmailCard() {
  const [s, setS] = useState<EmailStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get<EmailStatus>("/admin/emails").then((r) => setS(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(load, [load]);

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post<{ to: string; results: Record<string, boolean> }>("/admin/alerts/test");
      const ok = Object.values(data.results).filter(Boolean).length;
      (ok === 4 ? toast.success : toast.error)(`Alertas de prueba enviadas a ${data.to}: ${ok} de 4`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7" data-testid="admin-email-card">
      <h3 className="font-display text-base font-bold text-[#1B2A4A]">Correos</h3>
      {s && !s.resend_configured && (
        <p className="mt-3 flex gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="admin-resend-missing">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> Falta RESEND_API_KEY o EMAIL_FROM_ADDRESS: ningún correo se está enviando.
        </p>
      )}
      <Button onClick={sendTest} disabled={busy} variant="outline" className="mt-4" data-testid="admin-test-alerts-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Enviar alertas de prueba</>}
      </Button>
      <h4 className="mt-6 text-sm font-semibold text-slate-700">Correos fallidos</h4>
      {!s ? <div className="mt-2 h-16 animate-pulse rounded-lg bg-slate-100" /> : s.failures.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500" data-testid="admin-email-failures-empty">Sin correos fallidos.</p>
      ) : (
        <ul className="mt-2 max-h-80 space-y-3 overflow-y-auto" data-testid="admin-email-failures">
          {s.failures.map((f) => (
            <li key={f.id} className="border-l-2 border-rose-400 pl-3 text-xs text-slate-600" data-testid="admin-email-failure">
              <p className="font-mono text-slate-400">{new Date(f.created_at).toLocaleString("es-MX")}</p>
              <p className="font-semibold text-slate-700">{f.kind}</p>
              <p>{f.to}</p>
              <p className="text-rose-700">{f.reason}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
